/* REACT PACKAGE IMPORTS */
import { useEffect, useState, useRef, useMemo } from "react";
import { Container } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

/* INTERNAL IMPORTS */
import ConfirmBrowserExit from "../Components/ConfirmBrowserExit";
import Header from "../Components/Header";
import Loading from "../Components/Loading";
import SessionStart from "../Components/Sessions/SessionStart";
import SessionFinish from "../Components/Sessions/SessionFinish";
import SessionFixedBar from "../Components/Sessions/SessionFixedBar";
import {
  DateTimeFormatter,
  UTCDateTimeFormatter,
} from "../Components/DateTimeFormatter";

import { fetchAssessment } from "../Features/Assessments/Actions/AssessmentsActions";
import { fetchSession } from "../Features/Sessions/Actions/SessionsActions";

function SessionController({ sessionState, sessionStatus, sessionId }) {
  const sessionComponent = useMemo(() => {
    switch (sessionStatus) {
      case "started":
        return <SessionStart />;
      case "finished":
        return <SessionFinish />;
      case "expired":
        return <SessionFinish />;
      default:
        return <p>Session starting...</p>;
    }
  }, [sessionStatus, sessionId, sessionState]);

  return (
    <>
      {sessionComponent}
      <SessionFixedBar
        sessionStatus={sessionStatus}
        sessionState={sessionState}
      />
    </>
  );
}

export default function SessionCandidate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const sessionId = parseInt(id);
  const mounted = useRef(null);
  const initialized = useRef(false);
  const [isLoading, setIsLoading] = useState(true);

  const { sessionStatus, session } = useSelector((store) => store.sessions);

  const [sessionState, setSessionState] = useState(() => ({
    examDuration: "",
    timeToStartSession: null,
    details: {
      exid: null,
      title: "Default Title",
      subtitle: "Default Subtitle",
      localeDate: "Default locale date",
      UTCDate: "Default UTC date",
      startTime: 0,
      instructions: "Default Instructions",
      description: "Default Description",
      passmark: 100,
    },
  }));

  useEffect(() => {
    if (!sessionId || isNaN(sessionId)) {
      navigate("/candidate/error", { replace: true });
      return;
    }

    if (initialized.current) return;

    mounted.current = true;

    const initializeSession = async () => {
      try {
        const { schedule, status } = await dispatch(
          fetchSession(sessionId)
        ).unwrap();
        const { starttime, exam, description, title, referencetitle } =
          schedule;
        const { duration, instructions, exid, passmark } = exam;

        if (!mounted.current) return;

        document.title = referencetitle || "VX | FIFA";
        setSessionState((prev) => ({
          ...prev,
          examDuration: duration.stringshort,
          timeToStartSession: starttime,
          details: {
            exid: parseInt(exid),
            title: title || referencetitle,
            localeDate: DateTimeFormatter(starttime),
            UTCDate: UTCDateTimeFormatter(starttime),
            startTime: starttime,
            instructions,
            description,
            passmark,
          },
        }));

        if (exid && (status === "started" || status === "paused")) {
          await dispatch(fetchAssessment(exid));
        }
        initialized.current = true;
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to initialize session:", error);
        navigate("/candidate/error", { replace: true });
      }
    };

    initializeSession();

    return () => {
      mounted.current = false;
    };
  }, [sessionId, dispatch, navigate, session]);

  const headerProps = useMemo(
    () => ({
      title: sessionState.details.title,
      localeDate: sessionState.details.localeDate,
      UTCDate: sessionState.details.UTCDate,
      showBack: true,
    }),
    [sessionState.details.reference, sessionState.details.subtitle]
  );

  if (isLoading) {
    return (
      <section className="h-screen flex items-center justify-center">
        <Loading />
      </section>
    );
  }

  return (
    <Container className="pt-4 position-relative pb-24 w-[90%]">
      <ConfirmBrowserExit />
      <Header {...headerProps} />
      <SessionController
        sessionId={sessionId}
        sessionStatus={sessionStatus}
        sessionState={sessionState}
      />
    </Container>
  );
}
